'use client';

import React from 'react';

type BaseProps = {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  size?: 'regular' | 'small';
};

type AsButton = BaseProps & React.ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
type AsAnchor = BaseProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

type PrimaryButtonProps = AsButton | AsAnchor;

const BTN_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-poppins), Poppins, sans-serif',
  boxShadow: '0 3px 2px 0 rgba(250, 77, 117, 0.15)',
  border: '1px solid transparent',
  background:
    'linear-gradient(to right, #F52F81 0%, #FD7468 100%) padding-box, ' +
    'linear-gradient(to bottom, #FB5074 0%, #FD9CAE 100%) border-box',
  borderRadius: '20px',
};

const PrimaryButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, PrimaryButtonProps>(function PrimaryButton({
  children,
  className = '',
  disabled,
  style,
  size = 'regular',
  ...props
}, ref) {
  const sizeClassName = size === 'small'
    ? 'h-[44px] px-4 text-[14px] font-semibold'
    : 'py-[13px] px-6 text-[18px] font-semibold';

  const sharedClassName = [
    'group relative overflow-hidden',
    'inline-flex justify-center items-center gap-[8px]',
    sizeClassName,
    'text-white',
    disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'hover:brightness-[0.9] hover:scale-[0.98] transition-all duration-200 ease-in-out',
    className,
  ].join(' ');

  const sizeStyle: React.CSSProperties = size === 'small' ? { borderRadius: '16px' } : {};
  const sharedStyle = { ...BTN_STYLE, ...sizeStyle, ...style };

  const overlay = !disabled ? (
    <span
      className="absolute inset-0 opacity-0 group-hover:opacity-100"
      style={{ background: 'rgba(0,0,0,0.1)', transition: 'none', borderRadius: 'inherit' }}
      aria-hidden="true"
    />
  ) : null;

  if ('href' in props && props.href !== undefined) {
    const { href, ...anchorProps } = props as AsAnchor;
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        {...anchorProps}
        className={sharedClassName}
        style={sharedStyle}
      >
        {overlay}
        <span className={`relative whitespace-nowrap${disabled ? ' opacity-50' : ''}`}>{children}</span>
      </a>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      {...(props as AsButton)}
      disabled={disabled}
      className={sharedClassName}
      style={sharedStyle}
    >
      {overlay}
      <span className={`relative whitespace-nowrap${disabled ? ' opacity-50' : ''}`}>{children}</span>
    </button>
  );
});

PrimaryButton.displayName = 'PrimaryButton';

export default PrimaryButton;
